'use strict'

// React Imports
import {useEffect, useRef} from 'react'
import {useLocation} from 'react-router-dom'

// PWA OOTB Hooks
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'

// PWA Kit OOTB hooks
import {useCurrentBasket} from './use-current-basket'

// custom hooks
import useShipToCountry from './use-ship-to-country'

import {getDefaultShipmentID} from '../utils/basket-utils'

/**
 * Custom hook that resets the shipping address for the current shipment when the shipping country changes.
 *
 * This hook utilizes the `useEffect` hook to monitor changes in the desired shipping country. Upon detecting a change,
 * it checks if the country code of the shipping address in the first shipment of the basket matches the desired shipping country.
 * If there is a mismatch, it triggers an update to reset the shipping address for the shipment.
 *
 * @function useResetShippingAddress
 * @returns {void}
 */
const useResetShippingAddress = () => {
    const {shipToCountry} = useShipToCountry()
    const {data: basket} = useCurrentBasket()
    const DEFAULT_SHIPMENT_ID = getDefaultShipmentID(basket)

    const location = useLocation()
    const updateShippingAddressForShipment = useShopperBasketsMutation(
        'updateShippingAddressForShipment'
    )

    const isCheckout = /\/checkout$/.test(location?.pathname)

    // Ref to track if the component has mounted
    const hasMounted = useRef(false)

    useEffect(() => {
        // Skip the effect on the initial render
        if (!hasMounted.current) {
            hasMounted.current = true
            return
        }

        /**
         * Resets the shipping address for the current shipment if the country code of the existing
         * shipping address does not match the desired shipping country.
         *
         * This function checks the country code of the shipping address in the first shipment of the basket.
         * If it differs from the specified shipping country, it triggers an update to reset the shipping address.
         *
         * @function
         * @returns {void}
         */
        function resetShippingAddress() {
            if (
                basket != {} &&
                basket?.shipments &&
                basket?.shipments[0]?.shippingAddress?.countryCode &&
                basket?.shipments[0]?.shippingAddress?.countryCode != shipToCountry?.id &&
                !isCheckout
            ) {
                updateShippingAddressForShipment.mutate({
                    parameters: {
                        basketId: basket.basketId,
                        shipmentId: DEFAULT_SHIPMENT_ID
                    },
                    body: {}
                })
            }
        }
        resetShippingAddress()
    }, [
        shipToCountry?.id,
        basket != {} && basket?.shipments && basket?.shipments[0]?.shippingAddress?.countryCode
    ])
}

export default useResetShippingAddress
